import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Dropdown from 'react-bootstrap/Dropdown';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { postRequest, getRequest } from '../../../lib/api';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';
import { AuthContext } from '../../../authContext/context';
import { API_BASE, API_BASE_URL, CUSTOMER_PORTAL_URL } from '../../../lib/config';
import Swal from 'sweetalert2';
import Notes from '../Notes/note';
import ViewQuoteDetailModal from '../../../components/ViewQuoteDetailModal';
import jsPDF from 'jspdf';

// Revise Quote Modal Component for First Registration
function ReviseQuoteModal({ show, onHide, quoteFeeData, leadData, authToken, onReviseQuote, legalFeeData }) {
  const [fees, setFees] = useState({
    legal_fee: 0,
    identity_check: 0,
    hm_land_registry_fee: 0,
    land_registry_search: 0
  });
  const [totals, setTotals] = useState({
    net: 0,
    vat: 0,
    gross: 0,
    legalCost: { net: 0, vat: 0, gross: 0 },
    disbursementCost: { net: 0, vat: 0, gross: 0 }
  });
  const [isLoading, setIsLoading] = useState(false);

  // Initialize with quote fee data
  React.useEffect(() => {
    if (show && quoteFeeData) {
      const qf = quoteFeeData.fees || {};
      const qd = quoteFeeData.disbursements || {};
      const initialFees = {
        legal_fee: qf.legalFee?.amount || 0,
        identity_check: qf.identityCheck?.amount || 0,
        hm_land_registry_fee: qd.hmLandRegistryFee?.amount || 0,
        land_registry_search: qd.landRegistrySearch?.amount || 0
      };
      setFees(initialFees);
      calculateTotals(initialFees);
    }
  }, [quoteFeeData, show]);

  // Calculate totals
  const calculateTotals = (currentFees) => {
    const legalFeeNet = Number(currentFees.legal_fee) || 0;
    const legalFeeVAT = legalFeeNet * 0.20;

    // Use backend VAT values for fixed fees
    const identityCheckNet = Number(currentFees.identity_check) || 0;
    const identityCheckVAT = Number(quoteFeeData?.fees?.identityCheck?.vat) || 0;

    const hmLandRegistryFeeNet = Number(currentFees.hm_land_registry_fee) || 0;
    const hmLandRegistryFeeVAT = 0; // Government fee - no VAT

    const landRegistrySearchNet = Number(currentFees.land_registry_search) || 0;
    const landRegistrySearchVAT = 0; // No VAT for First Registration

    // Calculate Legal Cost
    const legalCostNet = legalFeeNet + identityCheckNet;
    const legalCostVAT = legalFeeVAT + identityCheckVAT;
    const legalCostGross = legalCostNet + legalCostVAT;

    // Calculate Disbursement Cost
    const disbursementCostNet = hmLandRegistryFeeNet + landRegistrySearchNet;
    const disbursementCostVAT = hmLandRegistryFeeVAT + landRegistrySearchVAT;
    const disbursementCostGross = disbursementCostNet + disbursementCostVAT;

    // Calculate Grand Totals
    const totalNet = legalCostNet + disbursementCostNet;
    const totalVAT = legalCostVAT + disbursementCostVAT;
    const totalGross = totalNet + totalVAT;

    setTotals({
      net: totalNet,
      vat: totalVAT,
      gross: totalGross,
      legalCost: { net: legalCostNet, vat: legalCostVAT, gross: legalCostGross },
      disbursementCost: { net: disbursementCostNet, vat: disbursementCostVAT, gross: disbursementCostGross }
    });
  };

  const handleReviseQuote = async () => {
    try {
      setIsLoading(true);

      const payload = {
        lead_id: leadData.lead_id || leadData._id,
        legal_fee: Number(fees.legal_fee),
        vat_amount: totals.vat,
        total_quote: totals.gross,
        // Include all fees for updating ConveyancingBranchManage
        fees: {
          legal_fee: Number(fees.legal_fee),
          identity_check: Number(fees.identity_check),
          hm_land_registry_fee: Number(fees.hm_land_registry_fee),
          land_registry_search: Number(fees.land_registry_search)
        },
        // Include branch and product info for updating ConveyancingBranchManage
        branch_id: legalFeeData?.branch_id || leadData?.branch_id,
        property_value: leadData?.property_value
      };

      console.log('📝 Modal submitting revised quote for First Registration:', payload);

      // Call the onReviseQuote callback
      await onReviseQuote(payload);

      // Close modal
      onHide();
    } catch (error) {
      console.error('❌ Modal error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <Modal.Title style={{ fontSize: '1.25rem', fontWeight: '600' }}>Revise Quote</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: '0 20px 20px 20px' }}>
        <div style={{ overflowX: 'auto' }}>
          <Table bordered hover style={{ marginBottom: 0 }}>
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', padding: '12px 15px', borderRight: '2px solid #dee2e6', width: '40%' }}>
                  First Registration <span style={{ color: '#dc3545', fontSize: '1.1rem' }}>+</span>
                </th>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>Net</th>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>VAT</th>
                <th style={{ fontWeight: '600', fontSize: '0.95rem', textAlign: 'center', padding: '12px 15px' }}>Gross</th>
              </tr>
            </thead>
            <tbody>
              {/* Legal Fees */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Legal Fees</td>
                <td style={{ padding: '8px', textAlign: 'center' }}>
                  <div style={{ width: '140px', margin: '0 auto', display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px' }}>£</span>
                    <input
                      type="number"
                      value={fees.legal_fee}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setFees(prev => ({ ...prev, legal_fee: newValue }));
                        calculateTotals({ ...fees, legal_fee: newValue });
                      }}
                      style={{ width: '100px', padding: '5px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{(Number(fees.legal_fee) * 0.20).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{(Number(fees.legal_fee) * 1.20).toFixed(2)}
                </td>
              </tr>

              {/* Fixed Fees Section */}
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td colSpan="4" style={{ padding: '8px 15px', fontWeight: '600', fontSize: '0.95rem' }}>
                  Fixed Fees <span style={{ color: '#dc3545', fontSize: '1.1rem' }}>+</span>
                </td>
              </tr>

              {/* Identity Check - Read-only */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Identity Check</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{Number(fees.identity_check).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{(quoteFeeData?.fees?.identityCheck?.vat || 0).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{(quoteFeeData?.fees?.identityCheck?.gross || 0).toFixed(2)}
                </td>
              </tr>

              {/* Disbursements Section */}
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td colSpan="4" style={{ padding: '8px 15px', fontWeight: '600', fontSize: '0.95rem' }}>
                  Disbursements <span style={{ color: '#dc3545', fontSize: '1.1rem' }}>+</span>
                </td>
              </tr>

              {/* HM Land Registry Fee - Non-editable */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>HM Land Registry Fee</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{Number(fees.hm_land_registry_fee).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £0.00
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{Number(fees.hm_land_registry_fee).toFixed(2)}
                </td>
              </tr>

              {/* Land Registry Search - Non-editable */}
              <tr>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>Land Registry Search</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £{Number(fees.land_registry_search).toFixed(2)}
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>
                  £0.00
                </td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem', fontWeight: '600' }}>
                  £{Number(fees.land_registry_search).toFixed(2)}
                </td>
              </tr>

              {/* TOTAL LEGAL COST */}
              <tr style={{ backgroundColor: '#fff3cd', fontWeight: '600', borderTop: '2px solid #dee2e6' }}>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>TOTAL LEGAL COST</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.legalCost.net.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.legalCost.vat.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.legalCost.gross.toFixed(2)}</td>
              </tr>

              {/* TOTAL DISBURSEMENT COST */}
              <tr style={{ backgroundColor: '#d1ecf1', fontWeight: '600' }}>
                <td style={{ padding: '12px 15px', fontSize: '0.95rem', borderRight: '2px solid #dee2e6' }}>TOTAL DISBURSEMENT COST</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.disbursementCost.net.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.disbursementCost.vat.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '12px 15px', fontSize: '0.95rem' }}>£{totals.disbursementCost.gross.toFixed(2)}</td>
              </tr>

              {/* Total (Legal cost + disbursement) */}
              <tr style={{ backgroundColor: '#d4edda', fontWeight: '700', fontSize: '1rem', borderTop: '2px solid #28a745' }}>
                <td style={{ padding: '15px', borderRight: '2px solid #dee2e6' }}>Total (Legal cost + disbursement)</td>
                <td style={{ textAlign: 'center', padding: '15px' }}>£{totals.net.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '15px' }}>£{totals.vat.toFixed(2)}</td>
                <td style={{ textAlign: 'center', padding: '15px' }}>£{totals.gross.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: 'none', padding: '0 20px 20px 20px', backgroundColor: '#f8f9fa' }}>
        <Button variant="outline-secondary" onClick={onHide} style={{ paddingLeft: '20px', paddingRight: '20px', fontWeight: '500' }}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={handleReviseQuote} disabled={isLoading} style={{ paddingLeft: '25px', paddingRight: '25px', fontWeight: '500' }}>
          {isLoading ? 'Updating...' : 'Revise Quote'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const ViewLeadFirstRegistration = () => {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteFeeData, setQuoteFeeData] = useState(null);
  const [loadingFee, setLoadingFee] = useState(true); // ✅ Start with true to show loading initially
  const [lenderPanels, setLenderPanels] = useState([]);
  const [reviseModalShow, setReviseModalShow] = useState(false);
  const [legalFeeData, setLegalFeeData] = useState(null);
  const [quoteHistory, setQuoteHistory] = useState([]);
  const [revisionCount, setRevisionCount] = useState(0);
  const [contactAttempts, setContactAttempts] = useState(0);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDetailModal, setShowQuoteDetailModal] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [viewEmailModal, setViewEmailModal] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    if (lead_id && authCtx.token) {
      fetchLeadData();
      fetchLenderPanels();
    }
  }, [lead_id, authCtx.token]);

  // Fetch quote fee, email templates and logs when leadData is loaded
  useEffect(() => {
    if (leadData && authCtx.token) {
      fetchQuoteFee(); // Automatically fetch quote on page load

      // ⏰ Delay quote history fetch by 10 seconds to allow initial calculation to be created
      const historyTimer = setTimeout(() => {
        console.log('⏰ 10 seconds passed, fetching quote history now...');
        fetchQuoteHistory();
      }, 10000); // 10 seconds delay

      fetchEmailTemplates();
      fetchEmailLogs();

      // Cleanup timer on unmount
      return () => clearTimeout(historyTimer);
    }
  }, [leadData, authCtx.token]);

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching First Registration lead data for:', lead_id);

      // Use get-single-lead API (same as Review of Legal Pack)
      const res = await postRequest(`private/get-single-lead/${lead_id}`, {}, authCtx.token);

      if ((res?.status === 200 || res?.status === 'success') && res?.data) {
        const data = res.data;
        setLeadData(data);
        console.log('✅ Lead data loaded:', data);
        console.log('🏠 Address:', data.address || data.property_address);
        console.log('👥 Customers:', data.customers);
        console.log('🏦 Lender Panel:', data.lender_panel_id);
        console.log('💰 Property Value:', data.property_value);

        // Initialize quote history and revision count
        setQuoteHistory(data.quote_history || []);
        setRevisionCount(data.revision_count || 0);
        console.log('📜 Quote History:', data.quote_history || []);
        console.log('🔢 Revision Count:', data.revision_count || 0);

        // Initialize contact attempts
        setContactAttempts(data.contact_attempts || 0);
        console.log('📞 Contact Attempts:', data.contact_attempts || 0);
      } else {
        console.log('⚠️ No lead data found');
      }
    } catch (err) {
      console.error('❌ Error fetching lead data:', err);
      Swal.fire('Error', 'Failed to load lead data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchLenderPanels = async () => {
    try {
      const response = await postRequest('private/getActiveLenderPanels', {}, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setLenderPanels(response.data);
        console.log('✅ Lender panels loaded:', response.data);
      }
    } catch (error) {
      console.error('❌ Error fetching lender panels:', error);
    }
  };

  const fetchQuoteFee = async () => {
    try {
      setLoadingFee(true);
      console.log('🔍 Fetching First Registration fee for:', lead_id);

      if (!leadData) {
        console.log('⚠️ No lead data available');
        return;
      }

      // Get partner_id if available
      const partnerId = typeof leadData.partner_id === 'object' ? leadData.partner_id?._id : leadData.partner_id;
      console.log('🤝 Partner ID:', partnerId);

      // Step 1: Extract postcode from lead's property address
      const address = leadData.property_address
        || leadData.propertyDetails?.address
        || leadData.address;
      let postcode = '';

      if (typeof address === 'object') {
        postcode = address.postcode || '';
      } else if (typeof address === 'string') {
        const parts = address.split(',');
        postcode = parts[parts.length - 1]?.trim() || '';
      }

      console.log('📮 Postcode from lead:', postcode);

      // Get product_id - find Conveyancing product
      const productsRes = await postRequest('private/getproductList', {}, authCtx.token);
      const conveyancingProduct = productsRes?.data?.find(product =>
        product.title?.toLowerCase().includes('conveyancing')
      );

      if (!conveyancingProduct) {
        console.log('❌ Conveyancing product not found');
        Swal.fire('Error', 'Conveyancing product not found', 'error');
        return;
      }

      console.log('📦 Product ID:', conveyancingProduct._id);

      // Get property value from lead data
      const propertyValue = leadData?.propertyDetails?.propertyValue
        || leadData?.propertyDetails?.property_value
        || leadData?.property_value
        || leadData?.conveyancing_details?.property_value
        || leadData?.conveyancing_details?.propertyDetails?.propertyValue
        || 0;
      console.log('💰 Property Value for fee calculation:', propertyValue);

      // Call API to calculate and save First Registration fee
      const requestBody = {
        lead_id: lead_id,
        service_type: 'First_Registration',
        product_id: conveyancingProduct._id,
        property_value: propertyValue
      };

      // Add partner_id if available, otherwise add postcode
      if (partnerId) {
        requestBody.partner_id = partnerId;
        console.log('🤝 Using partner_id:', partnerId);
      } else {
        if (!postcode) {
          Swal.fire('Error', 'No postcode found in lead data', 'error');
          return;
        }
        requestBody.postcode = postcode;
        console.log('🏢 Using postcode:', postcode);
      }

      console.log('📤 Fetching calculation from database...');

      // ✅ Fetch EXISTING calculation from database (don't create new one!)
      const fetchUrl = partnerId
        ? `private/fetch_lead_calculations/${lead_id}?partner_id=${partnerId}`
        : `private/fetch_lead_calculations/${lead_id}`;

      console.log('📞 Fetching from:', fetchUrl);
      const response = await getRequest(fetchUrl, authCtx.token);

      console.log('💰 API Response:', response);

      if (response?.status === 404) {
        // No calculation exists yet, create initial one
        console.log('⚠️ No calculation found (404), creating initial calculation...');
        const createResponse = await postRequest('private/calculateAndSaveLeadQuote', requestBody, authCtx.token);

        if (createResponse?.status !== 200 || !createResponse?.data) {
          console.log('⚠️ Failed to create initial calculation');
          Swal.fire('Error', createResponse?.message || 'Failed to fetch First Registration fee', 'error');
          return;
        }

        // Use the created calculation
        response.data = createResponse.data;
        console.log('✅ Initial calculation created:', response.data);
      } else if (response?.status !== 200 || !response?.data) {
        console.log('⚠️ No fee data returned');
        Swal.fire('Error', response?.message || 'Failed to fetch First Registration fee', 'error');
        return;
      } else {
        console.log('✅ Calculation fetched successfully:', response.data);
      }
      console.log('📊 Response structure:', {
        branch_name: response.data.branch_name,
        fees: response.data.fees,
        disbursements: response.data.disbursements,
        totals: response.data.totals
      });

      const quoteFeeDataToSet = {
        branch_name: response.data.branch_name,
        fees: response.data.fees,
        disbursements: response.data.disbursements,
        // Use totals from the new unified API response
        net: response.data.totals?.net || 0,
        vat: response.data.totals?.vat || 0,
        gross: response.data.totals?.gross || 0
      };

      console.log('💾 Setting quoteFeeData:', quoteFeeDataToSet);
      setQuoteFeeData(quoteFeeDataToSet);

      // Set legalFeeData from API response
      // NOTE: hmLandRegistryFee and landRegistrySearch are in disbursements, not fees
      const legalFeeDataToSet = {
        legal_fee: response.data.fees?.legalFee?.amount || 0,
        identity_check: response.data.fees?.identityCheck?.amount || 0,
        hm_land_registry_fee: response.data.disbursements?.hmLandRegistryFee?.amount || 0,
        land_registry_search: response.data.disbursements?.landRegistrySearch?.amount || 0,
        branch_name: response.data.branch_name,
        branch_id: response.data.branch_id
      };

      console.log('💾 Setting legalFeeData:', legalFeeDataToSet);
      setLegalFeeData(legalFeeDataToSet);

      console.log('✅ Fee data loaded successfully');

    } catch (err) {
      console.error('❌ Error fetching fee data:', err);
      Swal.fire('Error', err?.message || 'Failed to fetch quote details', 'error');
    } finally {
      setLoadingFee(false);
    }
  };

  const downloadQuotePDF = async () => {
    try {
      let pdfData = quoteFeeData;

      // If quoteFeeData is not available, fetch it first
      if (!pdfData) {
        console.log('📥 Quote data not loaded yet, fetching before download...');
        Swal.fire({
          title: 'Loading...',
          text: 'Fetching quote data for PDF generation',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        await fetchQuoteFee();

        // Wait for state to update
        await new Promise(resolve => setTimeout(resolve, 800));

        Swal.close();

        // Use the updated quoteFeeData from state
        pdfData = quoteFeeData;

        if (!pdfData) {
          Swal.fire('Error', 'Failed to load quote data for download', 'error');
          return;
        }
      }

      console.log('📄 Generating PDF with quote data:', pdfData);
      console.log('📊 Fees:', pdfData.fees);
      console.log('📊 Disbursements:', pdfData.disbursements);

      // Calculate totals from fees and disbursements
      const legalFeeAmount = pdfData.fees?.legalFee?.amount || 0;
      const legalFeeVAT = pdfData.fees?.legalFee?.vat || 0;
      const legalFeeGross = pdfData.fees?.legalFee?.gross || 0;

      const identityCheckAmount = pdfData.fees?.identityCheck?.amount || 0;
      const identityCheckVAT = pdfData.fees?.identityCheck?.vat || 0;
      const identityCheckGross = pdfData.fees?.identityCheck?.gross || 0;

      const hmLandRegAmount = pdfData.disbursements?.hmLandRegistryFee?.amount || 0;
      const hmLandRegVAT = pdfData.disbursements?.hmLandRegistryFee?.vat || 0;
      const hmLandRegGross = pdfData.disbursements?.hmLandRegistryFee?.gross || 0;

      const landRegSearchAmount = pdfData.disbursements?.landRegistrySearch?.amount || 0;
      const landRegSearchVAT = 0; // ✅ No VAT for First Registration
      const landRegSearchGross = landRegSearchAmount; // ✅ GROSS = NET (no VAT)

      // Calculate grand totals
      const totalNet = legalFeeAmount + identityCheckAmount + hmLandRegAmount + landRegSearchAmount;
      const totalVAT = legalFeeVAT + identityCheckVAT + hmLandRegVAT + landRegSearchVAT; // landRegSearchVAT = 0
      const totalGross = legalFeeGross + identityCheckGross + hmLandRegGross + landRegSearchGross;

      console.log('💰 Calculated Totals:', { totalNet, totalVAT, totalGross });

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPosition = 20;

      // Top header - Quote # and Generated date
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      const currentDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const currentTime = new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      doc.text(`Quote #L-${lead_id}; Generated ${currentDate} ${currentTime}`, margin, yPosition);
      yPosition += 10;

      // Lead ID
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text(`Lead ID: L-${lead_id}`, margin, yPosition);
      yPosition += 10;

      // Main Title - Quote for First Registration
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Quote for First Registration', margin, yPosition);
      yPosition += 15;

      // Description paragraph
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const description = `Thank you for requesting a conveyancing quote from My Law Matters, you will find a complete breakdown of our costs below. We would be delighted to act for you and one of our team will contact you shortly. In the meantime if you have any questions at all then please do get in touch on 0330 124 2171.`;
      const splitDescription = doc.splitTextToSize(description, pageWidth - (margin * 2));
      doc.text(splitDescription, margin, yPosition);
      yPosition += 25;

      // Section Header - FIRST REGISTRATION (in blue)
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(41, 128, 185); // Blue color
      doc.text('FIRST REGISTRATION', margin, yPosition);
      yPosition += 10;

      // Total Cost Summary
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Cost: £${totalGross.toFixed(2)} (£${totalNet.toFixed(2)} + £${totalVAT.toFixed(2)} VAT)`, margin, yPosition);
      yPosition += 15;

      // Calculate subtotals
      const legalCostNet = legalFeeAmount + identityCheckAmount;
      const legalCostVAT = legalFeeVAT + identityCheckVAT;
      const legalCostGross = legalFeeGross + identityCheckGross;

      const disbursementsNet = hmLandRegAmount + landRegSearchAmount;
      const disbursementsVAT = hmLandRegVAT + landRegSearchVAT;
      const disbursementsGross = hmLandRegGross + landRegSearchGross;

      // Manual Table Drawing
      const tableWidth = pageWidth - (margin * 2);
      // Adjust column widths - wider first column for long labels
      const col1Width = tableWidth * 0.50; // 50% for description
      const col2Width = tableWidth * 0.17; // 17% for Net
      const col3Width = tableWidth * 0.16; // 16% for VAT
      const col4Width = tableWidth * 0.17; // 17% for Total

      // Helper function to draw a row
      const drawRow = (label, net, vat, total, isBold = false, isHeader = false, bgColor = null) => {
        const font = isBold || isHeader ? 'bold' : 'normal';
        doc.setFont('helvetica', font);

        // Draw background if specified
        if (isHeader) {
          doc.setFillColor(240, 240, 240);
          doc.rect(margin, yPosition, tableWidth, 8, 'FD');
        } else if (bgColor) {
          doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
          doc.rect(margin, yPosition, tableWidth, 8, 'FD');
        }

        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);

        // Column borders with adjusted widths
        doc.rect(margin, yPosition, col1Width, 8, 'S');
        doc.rect(margin + col1Width, yPosition, col2Width, 8, 'S');
        doc.rect(margin + col1Width + col2Width, yPosition, col3Width, 8, 'S');
        doc.rect(margin + col1Width + col2Width + col3Width, yPosition, col4Width, 8, 'S');

        // Text with adjusted positions
        doc.text(label, margin + 2, yPosition + 5);
        doc.text(net, margin + col1Width + 2, yPosition + 5);
        doc.text(vat, margin + col1Width + col2Width + 2, yPosition + 5);
        doc.text(total, margin + col1Width + col2Width + col3Width + 2, yPosition + 5);

        yPosition += 8;
      };

      // Table header
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      drawRow('Description', 'Net', 'VAT', 'Total', false, true);

      // Legal Cost Section Header
      doc.setFillColor(248, 249, 250);
      doc.rect(margin, yPosition, tableWidth, 8, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.text('Legal Cost', margin + 2, yPosition + 5);
      yPosition += 8;

      // Legal Fee
      drawRow('Legal Fee', `£${legalFeeAmount.toFixed(2)}`, `£${legalFeeVAT.toFixed(2)}`, `£${legalFeeGross.toFixed(2)}`);

      // Identity Check
      drawRow('Identity Check', `£${identityCheckAmount.toFixed(2)}`, `£${identityCheckVAT.toFixed(2)}`, `£${identityCheckGross.toFixed(2)}`);

      // Legal Cost Total (with gray background)
      drawRow('TOTAL LEGAL COST', `£${legalCostNet.toFixed(2)}`, `£${legalCostVAT.toFixed(2)}`, `£${legalCostGross.toFixed(2)}`, true, false, [233, 236, 239]);

      // Disbursements Section Header
      doc.setFillColor(248, 249, 250);
      doc.rect(margin, yPosition, tableWidth, 8, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.text('Disbursement - Third Party Expenditure', margin + 2, yPosition + 5);
      yPosition += 8;

      // HM Land Registry Fee
      drawRow('HM Land Registry Fee', `£${hmLandRegAmount.toFixed(2)}`, `£${hmLandRegVAT.toFixed(2)}`, `£${hmLandRegGross.toFixed(2)}`);

      // Land Registry Search
      drawRow('Land Registry Search', `£${landRegSearchAmount.toFixed(2)}`, `£${landRegSearchVAT.toFixed(2)}`, `£${landRegSearchGross.toFixed(2)}`);

      // Disbursements Total (with gray background)
      drawRow('TOTAL DISBURSEMENT COST', `£${disbursementsNet.toFixed(2)}`, `£${disbursementsVAT.toFixed(2)}`, `£${disbursementsGross.toFixed(2)}`, true, false, [233, 236, 239]);

      // Grand Total (with blue background)
      doc.setFontSize(11);
      drawRow('TOTAL (Legal Cost + Disbursement Cost)', `£${totalNet.toFixed(2)}`, `£${totalVAT.toFixed(2)}`, `£${totalGross.toFixed(2)}`, true, false, [209, 236, 241]);

      // Save the PDF
      doc.save(`First_Registration_Quote_L${lead_id}.pdf`);

      console.log('✅ PDF downloaded successfully');
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      Swal.fire('Error', 'Failed to generate PDF', 'error');
    }
  };

  // Handle revise quote
  const handleReviseQuote = async (payload) => {
    try {
      console.log('🔄 ===== REVISE QUOTE (NEW API) =====');
      console.log('📦 Received payload from modal:', payload);

      // Transform flat fees to NESTED format specific to First Registration
      const nestedFees = {
        legalFee: {
          amount: Number(payload.fees.legal_fee || 0),
          vat: Number(payload.fees.legal_fee || 0) * 0.20,
          gross: Number(payload.fees.legal_fee || 0) * 1.20,
          label: 'Legal Fee'
        },
        identityCheck: {
          amount: Number(payload.fees.identity_check || 0),
          vat: Number(payload.fees.identity_check || 0) * 0.20,
          gross: Number(payload.fees.identity_check || 0) * 1.20,
          label: 'Identity Check'
        }
      };

      const nestedDisbursements = {
        hmLandRegistryFee: {
          amount: Number(payload.fees.hm_land_registry_fee || 0),
          vat: 0,
          gross: Number(payload.fees.hm_land_registry_fee || 0),
          label: 'HM Land Registry Fee'
        },
        landRegistrySearch: {
          amount: Number(payload.fees.land_registry_search || 0),
          vat: 0,
          gross: Number(payload.fees.land_registry_search || 0),
          label: 'Land Registry Search'
        }
      };

      // Calculate Legal Cost Total
      const legalCostNet = Object.values(nestedFees).reduce((sum, fee) => sum + fee.amount, 0);
      const legalCostVat = Object.values(nestedFees).reduce((sum, fee) => sum + fee.vat, 0);
      const legalCostGross = Object.values(nestedFees).reduce((sum, fee) => sum + fee.gross, 0);

      // Calculate Disbursement Cost Total
      const disbursementCostNet = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.amount, 0);
      const disbursementCostVat = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.vat, 0);
      const disbursementCostGross = Object.values(nestedDisbursements).reduce((sum, fee) => sum + fee.gross, 0);

      // Calculate Grand Total
      const totalNet = legalCostNet + disbursementCostNet;
      const totalVat = legalCostVat + disbursementCostVat;
      const totalGross = legalCostGross + disbursementCostGross;

      const nestedTotals = {
        legalCost: { net: legalCostNet, vat: legalCostVat, gross: legalCostGross },
        disbursementCost: { net: disbursementCostNet, vat: disbursementCostVat, gross: disbursementCostGross },
        total: { net: totalNet, vat: totalVat, gross: totalGross }
      };

      console.log('✅ Nested format created:', { nestedFees, nestedDisbursements, nestedTotals });

      // Call NEW reviseQuote API
      const apiUrl = `${API_BASE_URL}private/reviseQuote/${lead_id}`;
      console.log('📡 Calling NEW reviseQuote API:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        },
        body: JSON.stringify({
          fees: nestedFees,
          disbursements: nestedDisbursements,
          totals: nestedTotals
        })
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Revise Quote API Response:', result);

      if (result?.status === 200) {
        setReviseModalShow(false);

        // ✅ Refresh data instead of full page reload
        console.log('🔄 Refreshing quote data...');
        await fetchQuoteFee();

        // ⏰ Small delay before fetching history to allow backend to save
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchQuoteHistory();

        Swal.fire({
          title: 'Success',
          text: 'Quote revised successfully!',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire('Error', result?.message || 'Failed to revise quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error revising quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  // Handle Regenerate Quote - Restore Original Quote
  const handleRegenerateQuote = async () => {
    try {
      const confirmResult = await Swal.fire({
        title: 'Regenerate Original Quote?',
        text: 'This will restore the original quote values. Current changes will be saved to history.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, regenerate it!'
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      console.log('🔁 ===== REGENERATE QUOTE =====');
      console.log('📋 Lead ID:', lead_id);

      // Call regenerateQuote API
      const apiUrl = `${API_BASE_URL}private/regenerateQuote/${lead_id}`;
      console.log('📡 Calling regenerateQuote API:', apiUrl);

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('❌ API Error Response:', errorText);
        throw new Error(`API call failed: ${res.status} - ${errorText}`);
      }

      const result = await res.json();
      console.log('✅ Regenerate Quote API Response:', result);

      if (result.status === 200) {
        // ✅ Refresh data instead of full page reload
        console.log('🔄 Refreshing quote data after regeneration...');
        await fetchQuoteFee();

        // ⏰ Small delay before fetching history to allow backend to save
        await new Promise(resolve => setTimeout(resolve, 500));
        await fetchQuoteHistory();

        Swal.fire({
          title: 'Success',
          text: 'Original quote restored successfully!',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        Swal.fire('Error', result?.message || 'Failed to regenerate quote', 'error');
      }
    } catch (err) {
      console.error('❌ Error regenerating quote:', err);
      Swal.fire('Error', err?.message || 'Something went wrong', 'error');
    }
  };

  // Fetch Quote History from Database
  const fetchQuoteHistory = async () => {
    try {
      console.log('📚 Fetching quote history from database...');

      const response = await fetch(`${API_BASE_URL}private/getQuoteHistory/${lead_id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      });

      // Handle 404 specifically for new leads
      if (!response.ok) {
        if (response.status === 404) {
          console.log('ℹ️ No quote history yet (new lead)');
          setQuoteHistory([]); // Set empty array for new leads
        } else {
          console.warn('⚠️ Failed to fetch quote history');
          setQuoteHistory([]);
        }
        return;
      }

      const result = await response.json();

      if (result.status === 200 && result.data) {
        console.log('✅ Quote history fetched:', result.data);
        setQuoteHistory(result.data);
      } else {
        console.warn('⚠️ No quote history found');
        setQuoteHistory([]);
      }
    } catch (error) {
      console.error('❌ Error fetching quote history:', error);
      setQuoteHistory([]);
    }
  };

  // Handle contact attempt updates
  const handleUpdateContactAttempt = async (type) => {
    let updatedCount = contactAttempts;

    if (type === 'increase') updatedCount += 1;
    else if (type === 'reset') updatedCount = 0;

    try {
      const res = await postRequest('private/change-contact-status', {
        lead_id: lead_id,
        contact_attempts: updatedCount,
      }, authCtx.token);

      if (res.status === 200 || res.status === 'success') {
        setContactAttempts(updatedCount);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Fetch email templates from Automation actions
  const fetchEmailTemplates = async () => {
    try {
      console.log('📧 === FETCHING EMAIL TEMPLATES FOR FIRST REGISTRATION ===');

      let productId = leadData?.product_id?._id || leadData?.product_id;
      console.log('🔍 Product ID from leadData:', productId);

      if (!productId) {
        console.log('⚠️ Product ID not found in leadData, fetching products...');
        const productsRes = await postRequest('private/getAllProducts', {}, authCtx.token);

        if (productsRes?.data) {
          const conveyancingProduct = productsRes.data.find(p =>
            p.name?.toLowerCase().includes('conveyancing') ||
            p.product_name?.toLowerCase().includes('conveyancing')
          );

          if (conveyancingProduct) {
            productId = conveyancingProduct._id || conveyancingProduct.product_id;
            console.log('✅ Found Conveyancing product:', productId);
          }
        }
      }

      const automationUrl = `${API_BASE}/v1/admin/automationList`;

      const response = await fetch(automationUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authCtx.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId: productId,
          page: 1,
          limit: 100
        })
      });

      const responseData = await response.json();

      if (response.ok && (responseData?.status === 200 || responseData?.status === 'success')) {
        const automations = responseData.data?.docs || responseData.data || [];
        const emailTemplates = [];

        automations.forEach((automation) => {
          if (automation.effect && automation.effect.type?.toLowerCase().includes('send email')) {
            const template = {
              _id: automation.effect.emailTemplateId?._id || automation._id,
              name: automation.effect.emailTemplateId?.name || 'Unnamed Template',
              template_id: automation.effect.emailTemplateId?._id,
              automation_id: automation._id,
              effect: automation.effect
            };
            emailTemplates.push(template);
          }
        });

        console.log('📧 Total email templates extracted:', emailTemplates.length);
        setEmailTemplates(emailTemplates);
      } else {
        setEmailTemplates([]);
      }
    } catch (error) {
      console.error('❌ Error fetching email templates:', error);
      setEmailTemplates([]);
    }
  };

  // Fetch email logs/communication history
  const fetchEmailLogs = async () => {
    try {
      setLoadingEmails(true);
      console.log('📧 Fetching email logs for lead:', lead_id);

      const emailLogsResponse = await postRequest('private/getEmailLogs', {
        lead_id: lead_id,
        page: 1,
        limit: 100
      }, authCtx.token);

      const sentEmails = (emailLogsResponse?.status === 200 || emailLogsResponse?.status === 'success')
        ? (emailLogsResponse.data || [])
        : [];

      // 🚫 REMOVED: Don't show "Not triggered" automation placeholders
      // Only show actually sent emails
      setEmailLogs(sentEmails);
      setLoadingEmails(false);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setEmailLogs([]);
      setLoadingEmails(false);
    }
  };

  // Handle view email template
  const handleViewEmailTemplate = (emailLog) => {
    // Replace placeholders with actual data
    let content = emailLog.content || '';

    // Get customer data
    const customer = leadData?.customers?.[0] || {};
    const fullName = `${customer.forename || ''} ${customer.surname || ''}`.trim() || 'Customer';

    // Format current date
    const currentDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

    // Get property address
    let propertyAddress = leadData?.property_address || leadData?.address || '';
    if (typeof propertyAddress === 'object') {
      const parts = [
        propertyAddress.address1 || propertyAddress.line1,
        propertyAddress.address2 || propertyAddress.line2,
        propertyAddress.city || propertyAddress.town,
        propertyAddress.county || propertyAddress.country,
        propertyAddress.postcode
      ].filter(Boolean);
      propertyAddress = parts.join(', ');
    }

    // Format property price
    const propertyPrice = leadData?.property_value ? `£${Number(leadData.property_value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '';

    // Replace all placeholders (both single and double brace)
    const replacements = {
      'customer_title': customer.title || '',
      'customer_forename': customer.forename || '',
      'customer_surname': customer.surname || '',
      'customer_email': customer.email || '',
      'customer_phone': customer.phone_number || '',
      'customer_name': fullName,
      'client_name': fullName,
      'client': fullName,
      'ClientName': fullName,
      'date': currentDate,
      'reference': leadData?.lead_id || '',
      'lead_id': leadData?.lead_id || '',
      'property': propertyAddress,
      'property_address': propertyAddress,
      'price': propertyPrice,
      'property_value': propertyPrice,
      'tenure': leadData?.ownership_type || 'Freehold',
      'branch_name': leadData?.branch_id?.branchname || leadData?.branch_id?.name || 'My Law Matters',
      'branch_phone': leadData?.branch_id?.phone || '',
      'branch_email': leadData?.branch_id?.email || ''
    };

    Object.entries(replacements).forEach(([key, value]) => {
      content = content.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
      content = content.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
    });

    // Replace URL placeholders
    const portalUrl = CUSTOMER_PORTAL_URL;
    ['LeadInstructUrl', 'AutoLoginUrl', 'confirmation_link', 'customer_portal_link'].forEach(key => {
      content = content.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), portalUrl);
      content = content.replace(new RegExp(`\\{${key}\\}`, 'g'), portalUrl);
    });

    // Convert markdown links to HTML
    content = content.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" style="color: #667eea;">$1</a>');

    setSelectedEmail({ ...emailLog, content });
    setViewEmailModal(true);
  };

  // Send email using template
  const handleSendEmail = async (templateId, templateName) => {
    try {
      const confirmResult = await Swal.fire({
        icon: 'question',
        title: 'Send Email?',
        html: `<p>Are you sure you want to send <strong>"${templateName}"</strong> to the customer?</p>`,
        showCancelButton: true,
        confirmButtonText: 'Yes, Send Email',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      setSendingEmail(true);
      console.log('📧 Send Email clicked:', { templateId, templateName, lead_id });

      const customerEmails = leadData?.customers?.map(c => c.email).filter(e => e) || [];

      if (customerEmails.length === 0) {
        Swal.fire({
          icon: 'error',
          title: 'No Email Address',
          text: 'No customer email address found for this lead.'
        });
        setSendingEmail(false);
        return;
      }

      const response = await postRequest('private/sendEmailFromTemplate', {
        lead_id: lead_id,
        template_id: templateId,
        customer_emails: customerEmails
      }, authCtx.token);

      if (response?.status === 200 || response?.status === 'success') {
        Swal.fire({
          icon: 'success',
          title: 'Email Sent',
          text: `Email "${templateName}" has been sent successfully!`
        });
        fetchEmailLogs();
      }
    } catch (error) {
      console.error('Error sending email:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to send email'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDeleteLead = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete this lead #${lead_id}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        console.log('Attempting to delete lead with ID:', lead_id);

        // Use the DELETE method with fetch API
        const response = await fetch(`/api/v1/admin/private/delete-lead/${lead_id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${authCtx.token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('Delete response status:', response.status);

        let res;
        if (response.ok) {
          res = await response.json();
          console.log('Delete response data:', res);
        } else {
          // If the specific delete endpoint fails, log the error
          const errorText = await response.text();
          console.error('Delete failed with status:', response.status, 'Error:', errorText);
          throw new Error(`Delete failed with status ${response.status}`);
        }

        if (res && (res.status === 200 || res.status === 'success')) {
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'The lead has been deleted successfully.',
            timer: 1500,
            showConfirmButton: false
          });

          // Navigate back to leads list after a short delay
          setTimeout(() => {
            navigate('/leads');
          }, 1500);
        } else {
          throw new Error(res?.message || 'Failed to delete lead');
        }
      } catch (err) {
        console.error('Error deleting lead:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.message || 'An error occurred while deleting the lead.'
        });
      }
    }
  };

  const slugify = (text) =>
    text
      ?.toLowerCase()
      ?.replace(/ /g, '-')
      ?.replace(/[^\w-]+/g, '')
      ?.replace(/-+/g, '-');

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!leadData) {
    return (
      <div className="text-center mt-5">
        <p>No lead data found</p>
      </div>
    );
  }

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Leads', path: '/leads' },
          { label: `First Registration #${lead_id}`, path: '' },
        ]}
      />
      <br />

      <div className='page-header pb-3'>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className='mt-0 mb-2'>
              Conveyancing (First Registration) #{leadData?.lead_id || lead_id}
            </h1>
          </div>
        </div>
      </div>

      <div className='page-top-formview mb-3'>
        <div className='customer-tab-view leads-tab-view'>
          <Tabs defaultActiveKey="overview" id="first-registration-tabs">
            {/* OVERVIEW TAB */}
            <Tab eventKey="overview" title="Overview">
              <div className='mt-3'>
                {/* Quote Summary Box - Same style as Review of Legal Pack */}
                <div className="card shadow-sm mb-4" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                  <div className="card-header" style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #e9ecef',
                    padding: '1rem 1.25rem'
                  }}>
                    <h5 className="mb-0" style={{ color: '#495057', fontWeight: '600' }}>
                      Quote Summary
                    </h5>
                  </div>
                  <div className="card-body" style={{ padding: '1.5rem' }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-baseline gap-3">
                        <span className="text-muted small">Total Cost</span>
                        {loadingFee ? (
                          <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                            Loading...
                          </span>
                        ) : quoteFeeData ? (
                          <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                            £{(
                              (quoteFeeData.fees?.legalFee?.amount || 0) +
                              (quoteFeeData.fees?.identityCheck?.amount || 0) +
                              (quoteFeeData.disbursements?.hmLandRegistryFee?.amount || 0) +
                              (quoteFeeData.disbursements?.landRegistrySearch?.amount || 0)
                            ).toFixed(2)} Net + £{(
                              (quoteFeeData.fees?.legalFee?.vat || 0) +
                              (quoteFeeData.fees?.identityCheck?.vat || 0) +
                              (quoteFeeData.disbursements?.hmLandRegistryFee?.vat || 0)
                              // ✅ No VAT on Land Registry Search for First Registration
                            ).toFixed(2)} VAT = £{(
                              (quoteFeeData.fees?.legalFee?.gross || 0) +
                              (quoteFeeData.fees?.identityCheck?.gross || 0) +
                              (quoteFeeData.disbursements?.hmLandRegistryFee?.gross || 0) +
                              (quoteFeeData.disbursements?.landRegistrySearch?.amount || 0)  // ✅ Use amount (no VAT)
                            ).toFixed(2)} Gross
                          </span>
                        ) : (
                          <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '1.2rem' }}>
                            £{((leadData?.net_value || leadData?.legal_fee || 0)).toFixed(2)} Net + £{((leadData?.vat_amount || 0)).toFixed(2)} VAT = £{(leadData?.gross_value || leadData?.total_quote || 0).toFixed(2)} Gross
                          </span>
                        )}
                      </div>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          console.log('🔍 View Quote Detail clicked - fetching quote fee...');
                          fetchQuoteFee(); // Fetch quote data when clicked
                          setShowQuoteModal(true);
                        }}
                        style={{
                          color: '#0d6efd',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                      >
                        View Quote Detail
                      </a>
                    </div>

                    <hr className="my-3" />

                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span className="badge bg-secondary me-2">
                          <i className="fas fa-building me-1"></i>
                          {leadData?.branch_name || 'Manchester'}
                        </span>
                        <span className="badge bg-info">{leadData?.created_by || localStorage.getItem('username') || 'User'}</span>
                      </div>
                      <div className="text-muted small">
                        {(() => {
                          const date = leadData?.quote_date || leadData?.createdAt || leadData?.created_at;
                          if (!date) return 'Date not available';
                          const dateObj = new Date(date);
                          return `${dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="card-footer bg-light" style={{ borderTop: '1px solid #e9ecef', padding: '1rem 1.25rem' }}>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <Button
                        variant="contained"
                        className='text-capitalize'
                        onClick={async (e) => {
                          e.preventDefault();
                          console.log('🚀 Revise Quote clicked for First Registration');

                          // Fetch quote if not already loaded
                          if (!quoteFeeData) {
                            console.log('📥 Quote not loaded, fetching first...');
                            await fetchQuoteFee();
                          }

                          setReviseModalShow(true);
                        }}
                        style={{
                          backgroundColor: '#dc3545',
                          borderColor: '#dc3545',
                          fontSize: '0.875rem',
                          padding: '8px 16px'
                        }}
                      >
                        <EditOutlinedIcon fontSize="small" style={{ marginRight: '6px' }} />
                        Revise Quote
                      </Button>
                      <Button
                        variant="outlined"
                        className='text-capitalize'
                        onClick={handleRegenerateQuote}
                        style={{
                          color: '#6c757d',
                          borderColor: '#6c757d',
                          fontSize: '0.875rem',
                          padding: '8px 16px'
                        }}
                      >
                        <RefreshIcon fontSize="small" style={{ marginRight: '6px' }} />
                        Regenerate Quote
                      </Button>
                    </div>
                  </div>
                </div>
                <ul className="list-group mb-3">
                  {/* Address */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Address</div>
                      <div className="col-8">
                        {(() => {
                          const address = leadData?.property_address
                            || leadData?.address
                            || leadData?.conveyancing_details?.property_address;

                          if (!address) return 'No address specified';

                          if (typeof address === 'string') {
                            return address;
                          }

                          if (typeof address === 'object') {
                            const parts = [];
                            if (address.line1 || address.address1) parts.push(address.line1 || address.address1);
                            if (address.line2 || address.address2) parts.push(address.line2 || address.address2);
                            if (address.town) parts.push(address.town);
                            if (address.county) parts.push(address.county);
                            if (address.postcode) parts.push(address.postcode);
                            return parts.length > 0 ? parts.join(', ') : 'No address specified';
                          }

                          return 'No address specified';
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Customers */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Customers</div>
                      <div className="col-8">
                        {(() => {
                          const customers = leadData?.customers;
                          if (!customers || customers.length === 0) return 'No customers';

                          if (typeof customers === 'string') {
                            return customers;
                          }

                          if (Array.isArray(customers)) {
                            return customers.map((customer, index) => {
                              const customerData = typeof customer === 'object' ? customer : {};
                              const name = `${customerData?.title || ''} ${customerData?.forename || ''} ${customerData?.surname || ''}`.trim() || 'Unknown';
                              return (
                                <div key={index}>
                                  {name}
                                </div>
                              );
                            });
                          }

                          return 'No customers';
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Property Value */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Property Value</div>
                      <div className="col-8">
                        £{(leadData?.property_value || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </li>

                  {/* Lender Panel */}
                  {leadData?.lender_panel_id && (
                    <li className="list-group-item">
                      <div className="row">
                        <div className="col-4 fw-bold">Lender Panel</div>
                        <div className="col-8">
                          {(() => {
                            const lenderPanelId = leadData?.lender_panel_id;

                            if (lenderPanelId && typeof lenderPanelId === 'object' && lenderPanelId.name) {
                              return (
                                <div>
                                  <div style={{ color: '#495057' }}>
                                    {lenderPanelId.name}
                                  </div>
                                  {lenderPanelId?.lender_numbers && lenderPanelId.lender_numbers.length > 0 && (
                                    <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                        <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                      {lenderPanelId.lender_numbers.join(', ')}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            if (lenderPanelId && typeof lenderPanelId === 'string') {
                              const matchedPanel = lenderPanels.find(panel => panel._id === lenderPanelId);
                              if (matchedPanel) {
                                return (
                                  <div>
                                    <div style={{ color: '#495057' }}>
                                      {matchedPanel.name}
                                    </div>
                                    {matchedPanel?.lender_numbers && matchedPanel.lender_numbers.length > 0 && (
                                      <div style={{ color: '#6c757d', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                                        <span style={{ fontWeight: '500' }}>Lender Panel Number: </span>
                                        {matchedPanel.lender_numbers.join(', ')}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                            }

                            return <span className="text-muted">Not specified</span>;
                          })()}
                        </div>
                      </div>
                    </li>
                  )}

                  {/* Partner */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Partner</div>
                      <div className="col-8">
                        {leadData?.partner_id?.name || <span className="text-muted">Not specified</span>}
                      </div>
                    </div>
                  </li>

                  {/* Group Code */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Group Code</div>
                      <div className="col-8">
                        {(() => {
                          const groupCode = leadData?.group_code;

                          if (groupCode && typeof groupCode === 'object') {
                            return (
                              <span>
                                {groupCode.name} ({groupCode.code})
                              </span>
                            );
                          }

                          if (leadData?.group_code_code) {
                            return leadData.group_code_code;
                          }

                          return <span className="text-muted">Not specified</span>;
                        })()}
                      </div>
                    </div>
                  </li>

                  {/* Status */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Status</div>
                      <div className="col-8">
                        {leadData?.status || leadData?.lead_status || 'Pending'}
                      </div>
                    </div>
                  </li>

                  {/* Branch */}
                  <li className="list-group-item">
                    <div className="row">
                      <div className="col-4 fw-bold">Branch</div>
                      <div className="col-8">
                        {quoteFeeData?.branch_name || leadData?.branch_name || 'Not assigned'}
                      </div>
                    </div>
                  </li>
                </ul>

                {/* Edit Button and More Actions */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        console.log('Edit button clicked - First Registration');

                        // Get first customer ID if available - prioritize customer_id over _id
                        const firstCustomerId = leadData?.customers && Array.isArray(leadData.customers) && leadData.customers.length > 0
                          ? (leadData.customers[0].customer_id || leadData.customers[0]._id || leadData.customers[0])
                          : null;
                        const customerParam = firstCustomerId ? `?customerId=${firstCustomerId}` : '';

                        navigate(`/conveyancing/first-registration/${leadData?.lead_id || lead_id}${customerParam}`);
                      }}
                      style={{
                        borderColor: '#6c757d',
                        color: '#6c757d',
                        textTransform: 'none',
                        fontSize: '0.875rem'
                      }}
                    >
                      <EditOutlinedIcon fontSize="small" style={{ marginRight: '4px' }} />
                      Edit
                    </Button>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <span style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                      Created: {leadData?.created_at ? new Date(leadData.created_at).toLocaleDateString('en-GB') : 'Date not available'} - {leadData?.created_by || localStorage.getItem('username') || 'Unknown'}
                    </span>

                    <Dropdown drop="up">
                      <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        style={{
                          borderColor: '#6c757d',
                          color: '#6c757d',
                          fontSize: '0.875rem'
                        }}
                      >
                        More Actions
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => {
                            navigate('/leads/changestatus', {
                              state: {
                                leadId: lead_id,
                                currentStatus: leadData?.status,
                                returnUrl: window.location.pathname
                              }
                            });
                          }}
                        >
                          <UpdateIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Change Status
                        </Dropdown.Item>

                        <Dropdown.Item
                          onClick={() => {
                            navigate('/leads/changegroupcode', {
                              state: {
                                id: lead_id,
                                title: slugify(leadData?.product_title),
                                returnUrl: `/leads/view/first-registration/${lead_id}`
                              }
                            });
                          }}
                        >
                          <UpdateIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Change Group Code
                        </Dropdown.Item>

                        <Dropdown.Item onClick={handleDeleteLead} style={{ color: '#dc3545' }}>
                          <DeleteOutlineOutlinedIcon fontSize="small" style={{ marginRight: '8px' }} />
                          Delete Lead
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </Tab>

            {/* QUOTES TAB */}
            <Tab eventKey="quotes" title="Quotes">
              <div className="mt-3">
                {/* Current Quote Card */}
                {leadData && legalFeeData && (
                  <div className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h5 className="mb-1">
                            £{(() => {
                              // Calculate current total based on legalFeeData for First Registration
                              const legalFee = Number(legalFeeData.legal_fee || 0);
                              const identityCheck = Number(legalFeeData.identity_check || 0);
                              const hmLandRegistryFee = Number(legalFeeData.hm_land_registry_fee || 0);
                              const landRegistrySearch = Number(legalFeeData.land_registry_search || 0);

                              const totalNet = legalFee + identityCheck + hmLandRegistryFee + landRegistrySearch;
                              const legalFeeVAT = legalFee * 0.20;
                              const identityCheckVAT = identityCheck * 0.20;
                              const totalVAT = legalFeeVAT + identityCheckVAT;
                              const totalGross = totalNet + totalVAT;

                              return totalGross.toFixed(2);
                            })()}
                            {' '}
                            <span className="text-muted" style={{fontSize: '0.875rem'}}>
                              (£{Number(legalFeeData.legal_fee || 0).toFixed(2)} + £{(() => {
                                const legalFeeVAT = Number(legalFeeData.legal_fee || 0) * 0.20;
                                const identityCheckVAT = Number(legalFeeData.identity_check || 0) * 0.20;
                                return (legalFeeVAT + identityCheckVAT).toFixed(2);
                              })()})
                            </span>
                          </h5>
                          <p className="text-muted mb-0">
                            {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          {legalFeeData?.branch_name ? (
                            <span className="badge bg-secondary">{legalFeeData.branch_name}</span>
                          ) : (
                            <span className="badge bg-secondary">Partner rate card</span>
                          )}
                          <span className="badge bg-primary">{localStorage.getItem('username') || leadData?.created_by || 'User'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quote History Section - Enhanced UI */}
                {quoteHistory && quoteHistory.length > 0 && (
                  <div className="quote-history-section">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                        <i className="fas fa-history me-2" style={{ color: '#3498db' }}></i>
                        Quote History
                      </h5>
                      <span className="badge bg-primary" style={{ fontSize: '0.9rem', padding: '6px 12px' }}>
                        {quoteHistory.length} {quoteHistory.length === 1 ? 'Version' : 'Versions'}
                      </span>
                    </div>

                    <div className="timeline" style={{ position: 'relative', paddingLeft: '30px' }}>
                      {/* Timeline line */}
                      <div style={{
                        position: 'absolute',
                        left: '8px',
                        top: '20px',
                        bottom: '20px',
                        width: '2px',
                        backgroundColor: '#e0e0e0'
                      }}></div>

                      {quoteHistory.map((quote, index) => {
                        const totalGross = quote.totals?.total?.gross || 0;
                        const totalNet = quote.totals?.total?.net || 0;
                        const totalVat = quote.totals?.total?.vat || 0;
                        const legalCostGross = quote.totals?.legalCost?.gross || 0;
                        const isActive = quote.is_active || false;
                        const revisionNumber = quote.revision_number || 1;
                        const source = quote.calculation_source || 'api';

                        // Use revision_label directly from API
                        const versionLabel = quote.revision_label || `Revision ${revisionNumber}`;

                        // Format date
                        const displayDate = quote.revised_at || quote.createdAt;

                        // Icon and color based on type
                        let iconClass = 'fa-file-invoice';
                        let iconColor = '#95a5a6';
                        let borderColor = '#e0e0e0';

                        if (isActive) {
                          iconClass = 'fa-check-circle';
                          iconColor = '#27ae60';
                          borderColor = '#27ae60';
                        } else if (versionLabel === 'Original') {
                          iconClass = 'fa-star';
                          iconColor = '#f39c12';
                        } else if (source === 'manual_revision') {
                          iconClass = 'fa-edit';
                          iconColor = '#3498db';
                        }

                        return (
                          <div key={quote._id || index} style={{ position: 'relative', marginBottom: '24px' }}>
                            {/* Timeline dot */}
                            <div style={{
                              position: 'absolute',
                              left: '-26px',
                              top: '16px',
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: iconColor,
                              border: '3px solid white',
                              boxShadow: '0 0 0 2px ' + iconColor,
                              zIndex: 1
                            }}></div>

                            {/* Card */}
                            <div className="card" style={{
                              border: `2px solid ${isActive ? borderColor : '#e9ecef'}`,
                              borderRadius: '12px',
                              boxShadow: isActive ? '0 4px 12px rgba(39, 174, 96, 0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                              transition: 'all 0.3s ease',
                              backgroundColor: isActive ? '#f8fff9' : 'white'
                            }}>
                              <div className="card-body" style={{ padding: '20px' }}>
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  {/* Left: Version & Amount */}
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                      <i className={`fas ${iconClass}`} style={{ color: iconColor, fontSize: '18px' }}></i>
                                      <h5 className="mb-0" style={{ fontWeight: 600, color: '#2c3e50' }}>
                                        {versionLabel}
                                      </h5>
                                      {isActive && (
                                        <span className="badge" style={{
                                          backgroundColor: '#27ae60',
                                          color: 'white',
                                          padding: '4px 10px',
                                          borderRadius: '6px',
                                          fontSize: '0.75rem',
                                          fontWeight: 600
                                        }}>
                                          CURRENT
                                        </span>
                                      )}
                                    </div>

                                    {/* Date & Time */}
                                    <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                                      <i className="far fa-clock me-1"></i>
                                      {new Date(displayDate).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                      })} at {new Date(displayDate).toLocaleTimeString('en-GB', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>

                                    {/* Source Badge */}
                                    <span className="badge" style={{
                                      backgroundColor: source === 'manual_revision' ? '#3498db' : '#95a5a6',
                                      color: 'white',
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.7rem'
                                    }}>
                                      {source === 'manual_revision' ? 'MANUAL REVISION' :
                                       source === 'api' ? 'AUTO CALCULATED' :
                                       source.toUpperCase()}
                                    </span>
                                  </div>

                                  {/* Right: Branch & Action */}
                                  <div className="d-flex flex-column align-items-end gap-2">
                                    {quote.branch_name && (
                                      <span className="badge" style={{
                                        backgroundColor: '#e8f4f8',
                                        color: '#3498db',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600
                                      }}>
                                        <i className="fas fa-building me-1"></i>
                                        {quote.branch_name}
                                      </span>
                                    )}
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      style={{
                                        borderRadius: '8px',
                                        padding: '6px 16px',
                                        fontSize: '0.8rem',
                                        fontWeight: 500,
                                        borderWidth: '2px'
                                      }}
                                      onClick={() => {
                                        console.log('🔍 View Quote clicked for:', versionLabel);
                                        console.log('📦 Quote data:', quote);

                                        // Transform NESTED format to FLAT format for ViewQuoteDetailModal
                                        const fees = quote.fees || {};
                                        const disbursements = quote.disbursements || {};

                                        // Convert Map to Object if needed
                                        const feesObj = fees instanceof Map ? Object.fromEntries(fees) : fees;
                                        const disbursementsObj = disbursements instanceof Map ? Object.fromEntries(disbursements) : disbursements;

                                        // Calculate totals
                                        const totalVat = quote.totals?.total?.vat || 0;
                                        const totalGross = quote.totals?.total?.gross || 0;

                                        // ✅ For First Registration, only include relevant fees (NO TT fee!)
                                        // Filter to only First Registration fees
                                        const relevantFees = {
                                          legalFee: feesObj.legalFee,
                                          identityCheck: feesObj.identityCheck
                                        };

                                        const relevantDisbursements = {
                                          hmLandRegistryFee: disbursementsObj.hmLandRegistryFee,
                                          landRegistrySearch: disbursementsObj.landRegistrySearch
                                        };

                                        // ✅ Recalculate totals based on ONLY relevant fees
                                        const recalcLegalFeeNet = (relevantFees.legalFee?.amount || 0) + (relevantFees.identityCheck?.amount || 0);
                                        const recalcLegalFeeVat = (relevantFees.legalFee?.vat || 0) + (relevantFees.identityCheck?.vat || 0);
                                        const recalcLegalFeeGross = (relevantFees.legalFee?.gross || 0) + (relevantFees.identityCheck?.gross || 0);

                                        const recalcDisbursementNet = (relevantDisbursements.hmLandRegistryFee?.amount || 0) + (relevantDisbursements.landRegistrySearch?.amount || 0);
                                        const recalcDisbursementVat = 0; // ✅ No VAT on disbursements for First Registration
                                        const recalcDisbursementGross = recalcDisbursementNet; // ✅ GROSS = NET (no VAT)

                                        const recalcTotalNet = recalcLegalFeeNet + recalcDisbursementNet;
                                        const recalcTotalVat = recalcLegalFeeVat + recalcDisbursementVat;
                                        const recalcTotalGross = recalcLegalFeeGross + recalcDisbursementGross;

                                        const transformedFeeData = {
                                          branch_name: quote.branch_name,
                                          service_type: 'First_Registration',
                                          legal_fee: relevantFees.legalFee?.amount || 0,
                                          tt_fee: 0, // ❌ First Registration does NOT have TT fee
                                          identity_check: relevantFees.identityCheck?.amount || 0,
                                          hm_land_registry_fee: relevantDisbursements.hmLandRegistryFee?.amount || 0,
                                          land_registry_search: relevantDisbursements.landRegistrySearch?.amount || 0,
                                          land_charges_search: 0,
                                          official_copies: 0,
                                          vat_amount: recalcTotalVat, // ✅ Recalculated
                                          total_quote: recalcTotalGross, // ✅ Recalculated
                                          all_fees: relevantFees,
                                          all_disbursements: relevantDisbursements,
                                          totals: {
                                            legalCost: { net: recalcLegalFeeNet, vat: recalcLegalFeeVat, gross: recalcLegalFeeGross },
                                            disbursementCost: { net: recalcDisbursementNet, vat: recalcDisbursementVat, gross: recalcDisbursementGross },
                                            total: { net: recalcTotalNet, vat: recalcTotalVat, gross: recalcTotalGross }
                                          }
                                        };

                                        console.log('✅ Transformed fee data:', transformedFeeData);

                                        setSelectedQuote({
                                          legalFeeData: transformedFeeData,
                                          leadData: leadData,
                                          revision_label: versionLabel,
                                          is_history: !isActive
                                        });
                                        setShowQuoteDetailModal(true);
                                      }}
                                    >
                                      <VisibilityIcon fontSize="small" style={{ fontSize: '14px', marginRight: '4px' }} />
                                      View Details
                                    </Button>
                                  </div>
                                </div>

                                {/* Pricing Breakdown */}
                                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                                  <div className="row g-3">
                                    {/* Total Amount - Highlighted */}
                                    <div className="col-md-4">
                                      <div style={{
                                        backgroundColor: isActive ? '#e8f5e9' : '#f8f9fa',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        textAlign: 'center'
                                      }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                          Total Amount
                                        </p>
                                        <h4 className="mb-0" style={{ color: isActive ? '#27ae60' : '#2c3e50', fontWeight: 700 }}>
                                          £{Number(totalGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h4>
                                      </div>
                                    </div>

                                    {/* Legal Costs */}
                                    <div className="col-md-4">
                                      <div style={{ padding: '12px' }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                          <i className="fas fa-balance-scale me-1"></i>
                                          Legal Costs
                                        </p>
                                        <h6 className="mb-0" style={{ color: '#2c3e50', fontWeight: 600 }}>
                                          £{Number(legalCostGross).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h6>
                                      </div>
                                    </div>

                                    {/* VAT */}
                                    <div className="col-md-4">
                                      <div style={{ padding: '12px' }}>
                                        <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                          <i className="fas fa-receipt me-1"></i>
                                          VAT Included
                                        </p>
                                        <h6 className="mb-0" style={{ color: '#2c3e50', fontWeight: 600 }}>
                                          £{Number(totalVat).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </h6>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* No quotes message */}
                {(!leadData || !legalFeeData) && (!quoteHistory || quoteHistory.length === 0) && (
                  <div className="text-center py-5">
                    <i className="fas fa-file-invoice fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Quotes Available</h5>
                    <p className="text-muted">No quotes have been generated yet.</p>
                  </div>
                )}
              </div>
            </Tab>

            {/* NOTES TAB */}
            <Tab eventKey="notes" title="Notes">
              <Notes lead_id={lead_id} product_id={leadData?.product_id} />
            </Tab>

            {/* COMMUNICATION TAB */}
            <Tab eventKey="communication" title="Communication">
              <div className='communication-table'>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Dropdown>
                    <Dropdown.Toggle variant="secondary" id="dropdown-basic">
                      Contact Attempt ({contactAttempts})
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('increase')}>
                        <MailOutlineIcon fontSize="small" /> &nbsp; Increase Contact Attempt Count
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => handleUpdateContactAttempt('reset')}>
                        <CallOutlinedIcon fontSize="small" /> &nbsp; Reset
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Send Email Dropdown - Right Aligned */}
                  <div className="d-flex align-items-center gap-2">
                    <Dropdown>
                      <Dropdown.Toggle variant="primary" id="send-email-dropdown" disabled={sendingEmail}>
                        {sendingEmail ? 'Sending...' : 'Send Email'}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        {emailTemplates.length === 0 ? (
                          <Dropdown.Item disabled>No templates available (Count: {emailTemplates.length})</Dropdown.Item>
                        ) : (
                          emailTemplates.map((template) => (
                            <Dropdown.Item
                              key={template._id}
                              onClick={() => handleSendEmail(template._id, template.name)}
                            >
                              {template.name}
                            </Dropdown.Item>
                          ))
                        )}
                      </Dropdown.Menu>
                    </Dropdown>

                    {/* Refresh Button for debugging */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        console.log('🔄 Manual refresh triggered');
                        fetchEmailTemplates();
                      }}
                      title="Refresh templates"
                    >
                      <RefreshIcon fontSize="small" />
                    </Button>
                  </div>
                </div>

                {/* Email History Table */}
                <div className="mt-4">
                  <Table striped responsive bordered>
                    <thead>
                      <tr>
                        <th>Sent</th>
                        <th>Customer</th>
                        <th>Content</th>
                        <th style={{ width: '80px', textAlign: 'center' }}>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingEmails ? (
                        <tr>
                          <td colSpan="4" className="text-center">Loading...</td>
                        </tr>
                      ) : emailLogs.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center">No communication logs found</td>
                        </tr>
                      ) : (
                        emailLogs.map((log, index) => (
                          <tr key={log._id || index}>
                            <td>
                              {log.sent_at ? new Date(log.sent_at).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : (log.isAutomation ? <span className="text-muted">Not triggered</span> : 'N/A')}
                            </td>
                            <td>
                              <div>
                                {log.recipient_name || log.customer_id?.forename || 'Unknown'}
                                <div className="text-muted small">{log.recipient_email}</div>
                              </div>
                            </td>
                            <td>
                              <div>
                                <strong>{log.template_name || log.subject || 'No title'}</strong>
                                <div className="text-muted small">
                                  {log.subject && log.subject !== log.template_name && (
                                    <span>Subject: {log.subject}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewEmailTemplate(log)}
                                title="View email content"
                                sx={{ minWidth: '40px', padding: '4px 8px' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Tab>

            {/* TASKS TAB */}
            <Tab eventKey="tasks" title="Tasks">
              <div className="mt-3">
                <div className="text-center py-5">
                  <h5 className="text-muted">Tasks Functionality Coming Soon</h5>
                  <p className="text-muted">Task management features will be available here.</p>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>

      {/* View Quote Modal */}
      <Modal show={showQuoteModal} onHide={() => setShowQuoteModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>View Quote</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingFee ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading quote details...</p>
            </div>
          ) : quoteFeeData ? (
            <>
              <Table bordered hover style={{ marginBottom: '0' }}>
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th style={{ fontWeight: '600', width: '40%' }}>FIRST REGISTRATION</th>
                    <th style={{ fontWeight: '600', textAlign: 'center' }}>NET</th>
                    <th style={{ fontWeight: '600', textAlign: 'center' }}>VAT</th>
                    <th style={{ fontWeight: '600', textAlign: 'center' }}>GROSS</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Legal Cost Section Header */}
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td colSpan="4"><strong>Legal Cost</strong></td>
                  </tr>

                  {/* Legal Fee */}
                  <tr>
                    <td>Legal Fee</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.fees?.legalFee?.amount || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.fees?.legalFee?.vat || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.fees?.legalFee?.gross || 0).toFixed(2)}</td>
                  </tr>

                  {/* Identity Check */}
                  <tr>
                    <td>Identity Check</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.fees?.identityCheck?.amount || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.fees?.identityCheck?.vat || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.fees?.identityCheck?.gross || 0).toFixed(2)}</td>
                  </tr>

                  {/* Legal Cost Total */}
                  <tr style={{ backgroundColor: '#e9ecef', fontWeight: '600' }}>
                    <td>TOTAL LEGAL COST</td>
                    <td style={{ textAlign: 'center' }}>
                      £{((quoteFeeData.fees?.legalFee?.amount || 0) + (quoteFeeData.fees?.identityCheck?.amount || 0)).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{((quoteFeeData.fees?.legalFee?.vat || 0) + (quoteFeeData.fees?.identityCheck?.vat || 0)).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{((quoteFeeData.fees?.legalFee?.gross || 0) + (quoteFeeData.fees?.identityCheck?.gross || 0)).toFixed(2)}
                    </td>
                  </tr>

                  {/* Disbursements Section Header */}
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <td colSpan="4"><strong>Disbursement - Third Party Expenditure</strong></td>
                  </tr>

                  {/* HM Land Registry Fee */}
                  <tr>
                    <td>HM Land Registry Fee</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.disbursements?.hmLandRegistryFee?.amount || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.disbursements?.hmLandRegistryFee?.vat || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.disbursements?.hmLandRegistryFee?.gross || 0).toFixed(2)}</td>
                  </tr>

                  {/* Land Registry Search */}
                  <tr>
                    <td>Land Registry Search</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.disbursements?.landRegistrySearch?.amount || 0).toFixed(2)}</td>
                    <td style={{ textAlign: 'center' }}>£0.00</td>
                    <td style={{ textAlign: 'center' }}>£{(quoteFeeData.disbursements?.landRegistrySearch?.amount || 0).toFixed(2)}</td>
                  </tr>

                  {/* Disbursements Total */}
                  <tr style={{ backgroundColor: '#e9ecef', fontWeight: '600' }}>
                    <td>TOTAL DISBURSEMENT COST</td>
                    <td style={{ textAlign: 'center' }}>
                      £{((quoteFeeData.disbursements?.hmLandRegistryFee?.amount || 0) + (quoteFeeData.disbursements?.landRegistrySearch?.amount || 0)).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £0.00
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{((quoteFeeData.disbursements?.hmLandRegistryFee?.amount || 0) + (quoteFeeData.disbursements?.landRegistrySearch?.amount || 0)).toFixed(2)}
                    </td>
                  </tr>

                  {/* Grand Total */}
                  <tr style={{ backgroundColor: '#d1ecf1', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    <td>TOTAL (Legal Cost + Disbursement Cost)</td>
                    <td style={{ textAlign: 'center' }}>
                      £{(
                        (quoteFeeData.fees?.legalFee?.amount || 0) +
                        (quoteFeeData.fees?.identityCheck?.amount || 0) +
                        (quoteFeeData.disbursements?.hmLandRegistryFee?.amount || 0) +
                        (quoteFeeData.disbursements?.landRegistrySearch?.amount || 0)
                      ).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{(
                        (quoteFeeData.fees?.legalFee?.vat || 0) +
                        (quoteFeeData.fees?.identityCheck?.vat || 0) +
                        (quoteFeeData.disbursements?.hmLandRegistryFee?.vat || 0)
                        // ✅ No VAT on Land Registry Search for First Registration
                      ).toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      £{(
                        (quoteFeeData.fees?.legalFee?.gross || 0) +
                        (quoteFeeData.fees?.identityCheck?.gross || 0) +
                        (quoteFeeData.disbursements?.hmLandRegistryFee?.gross || 0) +
                        (quoteFeeData.disbursements?.landRegistrySearch?.amount || 0)  // ✅ Use amount (no VAT)
                      ).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </>
          ) : (
            <div className="text-center py-3">
              <p className="text-muted">No quote data available</p>
            </div>
          )}

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="outlined"
              size="small"
              onClick={downloadQuotePDF}
              disabled={!quoteFeeData}
              style={{
                borderColor: '#6c757d',
                color: '#6c757d',
                textTransform: 'none'
              }}
            >
              <i className="fas fa-download me-2"></i>
              Download
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Revise Quote Modal */}
      <ReviseQuoteModal
        show={reviseModalShow}
        onHide={() => setReviseModalShow(false)}
        quoteFeeData={quoteFeeData}
        leadData={leadData}
        authToken={authCtx.token}
        onReviseQuote={handleReviseQuote}
        legalFeeData={legalFeeData}
      />

      {/* View Email Modal */}
      <Modal show={viewEmailModal} onHide={() => setViewEmailModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{selectedEmail?.template_name || selectedEmail?.subject || 'Email Content'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: selectedEmail?.content || '<p>No content available</p>' }} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewEmailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Quote Detail Modal */}
      <ViewQuoteDetailModal
        show={showQuoteDetailModal}
        onHide={() => {
          console.log('Closing ViewQuoteDetailModal');
          setShowQuoteDetailModal(false);
          setSelectedQuote(null);
        }}
        leadId={leadData?._id || lead_id}
        authToken={authCtx.token}
        legalFeeData={selectedQuote?.legalFeeData || legalFeeData}
        leadData={selectedQuote?.leadData || leadData}
        calculationTotals={selectedQuote?.legalFeeData?.totals || null}
      />
    </>
  );
};

export default ViewLeadFirstRegistration;
